import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Leaf, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { GoogleAuthButton } from "../components/GoogleAuthButton";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(form);
      toast.success("Account created! Let's complete your health profile.");
      nav("/health-profile", { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2" data-testid="register-page">
      <div className="flex items-center justify-center p-6 lg:p-12 order-2 lg:order-1">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <h1 className="font-heading text-3xl font-semibold text-ink">Create your free account</h1>
          <p className="mt-2 text-muted2">Already have one? <Link to="/login" className="text-moss font-medium hover:underline">Log in</Link></p>
          <div className="mt-8">
            <GoogleAuthButton label="Sign up with Google" testId="register-google-button" />
            <div className="my-6 flex items-center gap-3 text-xs text-soft">
              <span className="flex-1 h-px bg-subtle" />
              or use email
              <span className="flex-1 h-px bg-subtle" />
            </div>
          </div>
          <div className="space-y-5">
            {[
              ["full_name", "Full name", "text"],
              ["email", "Email", "email"],
              ["phone", "Phone number", "tel"],
              ["password", "Password (min 6 chars)", "password"],
            ].map(([k, label, type]) => (
              <div key={k}>
                <label className="block text-sm font-medium text-ink mb-2">{label}</label>
                <input type={type} required value={form[k]} onChange={upd(k)}
                  data-testid={`register-${k}-input`}
                  className="w-full rounded-xl border border-subtle bg-white px-4 py-3 text-ink focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20 transition-all" />
              </div>
            ))}
            {error && <p className="text-sm text-danger" data-testid="register-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="register-submit-button"
              className="w-full inline-flex justify-center items-center gap-2 bg-terra hover:bg-terra-dark text-white rounded-full px-6 py-3 font-semibold transition-all active:scale-95 disabled:opacity-60">
              {busy && <Loader2 className="animate-spin" size={18} />} Create account
            </button>
            <p className="text-xs text-soft text-center">By signing up you agree to our Terms & Privacy Policy.</p>
          </div>
        </form>
      </div>

      <div className="hidden lg:flex flex-col justify-between bg-moss text-white p-12 relative overflow-hidden order-1 lg:order-2 min-h-[600px]">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-terra/30 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-full bg-white/20 grid place-items-center"><Leaf size={18} /></span>
            <span className="font-heading text-xl font-semibold">Danashakaa.com</span>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-heading text-3xl lg:text-4xl font-semibold leading-tight max-w-md">
            Free profile, free preview. Upgrade when you're ready.
          </h2>
          <p className="mt-4 text-white/80 max-w-md">
            We'll guide you through a short health profile, then unlock the locked previews when you subscribe.
          </p>
        </div>
      </div>
    </div>
  );
}
